import { useNavigate } from "react-router-dom";
import summaryIllustration from "@/assets/froppio-summary.svg";
import { useAuth } from "@/core/auth/hooks/useAuth";
import type { Business } from "@/features/business/types/businessTypes";
import type { BusinessLocation } from "@/features/business/types/businessLocationTypes";
import type { Profile } from "@/features/profile/types/profileTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { DetailSummary } from "@/shared/components/ui/DetailSummary";
import { Page, PageContent, PageFooter } from "@/shared/components/ui/Page";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { SetupHeader } from "@/shared/components/ui/SetupHeader";

interface OnboardingSummaryPageProps {
  profile: Profile;
  business: Business;
  location: BusinessLocation;
  onComplete: () => void;
  onEditProfile: () => void;
  onEditBusiness: () => void;
  onEditLocation: () => void;
}

export function OnboardingSummaryPage({
  profile, business, location, onComplete, onEditProfile, onEditBusiness, onEditLocation,
}: OnboardingSummaryPageProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const address = [location.address, location.city, location.state, location.postalCode]
    .filter((value) => value?.trim()).join("\n");

  const handleGoToDashboard = () => {
    onComplete();
    navigate(`/b/${business.id}/l/${location.id}`, { replace: true });
  };

  return (
    <div className="h-dvh">
      <Page>
        <SetupHeader title="Looking good!" illustration={summaryIllustration} step={4} stage="Review" />
        <PageContent>
          <div className="mx-auto w-full max-w-md space-y-[26px]">
            <Section title="Your profile" card action={<SectionAction onClick={onEditProfile}>Edit profile</SectionAction>}>
              <DetailSummary items={[
                { label: "First name", value: profile.firstName || "Not provided" },
                { label: "Last name", value: profile.lastName || "Not provided" },
                ...(user?.email ? [{ label: "Account email", value: user.email }] : []),
              ]} />
            </Section>
            <Section title="Your business" card action={<SectionAction onClick={onEditBusiness}>Edit business</SectionAction>}>
              <DetailSummary items={[
                { label: "Business name", value: business.name },
                { label: "Industry", value: business.industry || "Not provided" },
                { label: "Timezone", value: business.timezone },
                { label: "Currency", value: business.currency },
              ]} />
            </Section>
            <Section title="Your first location" card action={<SectionAction onClick={onEditLocation}>Edit location</SectionAction>}>
              <DetailSummary items={[
                { label: "Location name", value: location.name },
                { label: "Address", value: <span className="whitespace-pre-wrap">{address || "Not provided"}</span> },
              ]} />
            </Section>
          </div>
        </PageContent>
        <PageFooter narrow>
          <Button size="touch" onClick={handleGoToDashboard}>Go to dashboard</Button>
        </PageFooter>
      </Page>
    </div>
  );
}
